import { useState, useCallback } from 'react';
import { pdf } from '@react-pdf/renderer';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { selectClient, selectSessionAdvisor } from '@/model';
import { OnboardingPdfReport } from '@/features/pdf-report/components/onboarding-pdf-report';
import { renderIllustrationsToPng } from '@/features/pdf-report/lib/render-illustrations-to-png';
import { getLtcCostRoundingStep } from '@shared';
import { analytics } from '@shared/posthog';
import { useGuidedPlanningSession } from '@/features/guided-financial-planning';
import { usePdfReportSections } from '@/features/client-onboarding';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { pdfAuditApi, pdfReportApi } from '@/api/waterlily';

interface EdjEmailSentState {
    isOpen: boolean;
    advisorEmail?: string;
}

async function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result as string;
            resolve(dataUrl.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

export function usePdfReportDownload() {
    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [edjEmailSent, setEdjEmailSent] = useState<EdjEmailSentState>({ isOpen: false });
    const client = useSelector(selectClient);
    const sessionAdvisor = useSelector(selectSessionAdvisor);
    const { session: guidedPlanningSession } = useGuidedPlanningSession(client?.clientId ?? '');
    const { includeCareJourney, includePredictedCareNeeds } = usePdfReportSections();
    const isEdjPilot = useIsEdjPilotAdvisor();

    const downloadPdfReport = useCallback(async () => {
        if (!client) return;

        setIsDownloading(true);
        setError(null);

        try {
            const roundingStep = getLtcCostRoundingStep(
                client.clientAdvisorSettings?.ltcCostDisplayPrecision,
            );
            const rawAllocations =
                guidedPlanningSession?.stepData?.planComparisonSnapshot?.fundingAllocations ?? [];
            const multipleFundingSourcesSnapshot = guidedPlanningSession?.stepData
                ?.multipleFundingSourcesSnapshot as
                | { selfFunding?: Record<string, { existingAssetsValue?: number }> }
                | undefined;

            const illustrations = await renderIllustrationsToPng();

            const blob = await pdf(
                OnboardingPdfReport({
                    client,
                    advisor: {
                        organizationDisplayName: sessionAdvisor?.organizationDisplayName,
                        organizationName: sessionAdvisor?.organizationName,
                        customDisclosures: sessionAdvisor?.customDisclosures,
                    },
                    illustrations,
                    options: {
                        ltcCostRoundingStep: roundingStep,
                        includeCareJourney,
                        includePredictedCareNeeds,
                        guidedFundingAllocations: rawAllocations,
                        multipleFundingSourcesSnapshot,
                    },
                }),
            ).toBlob();

            const filename = `${client.clientFirstName} ${client.clientLastName} LTC Report.pdf`;
            const pdfBase64 = await blobToBase64(blob);

            if (isEdjPilot && sessionAdvisor?.advisorId) {
                await pdfReportApi.emailToAdvisor(sessionAdvisor.advisorId, {
                    pdfBase64,
                    clientEmail: client.clientEmail ?? '',
                    clientName: `${client.clientFirstName ?? ''} ${client.clientLastName ?? ''}`.trim(),
                    filename,
                });
                setEdjEmailSent({ isOpen: true, advisorEmail: sessionAdvisor.advisorEmail });

                analytics.reports.pdfEmailed({
                    clientId: client.clientId,
                    includeCareJourney,
                    includePredictedCareNeeds,
                });

                void pdfAuditApi
                    .log(client.clientId, {
                        pdfBase64,
                        pdfType: 'ltc_report',
                        action: 'email',
                        filename,
                        recipientEmail: sessionAdvisor.advisorEmail ?? null,
                    })
                    .catch(auditErr => {
                        console.error('Failed to record LTC report email audit log', auditErr);
                    });
            } else {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                analytics.reports.pdfDownloaded({
                    clientId: client.clientId,
                    includeCareJourney,
                    includePredictedCareNeeds,
                });

                void pdfAuditApi
                    .log(client.clientId, {
                        pdfBase64,
                        pdfType: 'ltc_report',
                        action: 'download',
                        filename,
                    })
                    .catch(auditErr => {
                        console.error('Failed to record LTC report download audit log', auditErr);
                    });
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to generate PDF';
            setError(message);
            toast.error('Failed to generate report. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    }, [client, sessionAdvisor, guidedPlanningSession, includeCareJourney, includePredictedCareNeeds, isEdjPilot]);

    const dismissEdjEmailSent = useCallback(() => {
        setEdjEmailSent(prev => ({ ...prev, isOpen: false }));
    }, []);

    return {
        downloadPdfReport,
        isDownloading,
        error,
        edjEmailSent,
        dismissEdjEmailSent,
    };
}
